import * as Dialog from "@radix-ui/react-dialog";
import { AlertCircle, X } from "lucide-react";
import { useEffect, useState } from "react";

interface PasteVideoLinkDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (url: string) => void;
}

export function PasteVideoLinkDialog({ open, onOpenChange, onSubmit }: PasteVideoLinkDialogProps) {
  const [url, setUrl] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open) {
      setUrl("");
      setError("");
    }
  }, [open]);

  const validateUrl = (urlString: string): boolean => {
    try {
      const parsed = new URL(urlString);
      // Check if it's a YouTube URL or a video file URL
      const isYouTube =
        parsed.hostname.includes("youtube.com") || parsed.hostname.includes("youtu.be");
      const isVideoFile =
        urlString.endsWith(".mp4") ||
        urlString.endsWith(".webm") ||
        urlString.endsWith(".ogg") ||
        urlString.endsWith(".mov");

      return isYouTube || isVideoFile;
    } catch {
      return false;
    }
  };

  const handleSubmit = () => {
    if (!url.trim()) {
      setError("Please enter a URL");
      return;
    }

    if (!validateUrl(url)) {
      setError("Please enter a valid YouTube URL or video file URL (MP4, WebM, etc.)");
      return;
    }

    onSubmit(url);
    setUrl("");
    setError("");
    onOpenChange(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSubmit();
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <Dialog.Content className="fixed left-[50%] top-[50%] z-50 w-full max-w-sm translate-x-[-50%] translate-y-[-50%] rounded-lg border border-border bg-card shadow-2xl">
          {/* Header */}
          <div className="border-b border-border p-5">
            <Dialog.Title className="text-lg font-semibold text-foreground">
              Paste Video Link
            </Dialog.Title>
            <p className="mt-1 text-sm text-muted-foreground">
              YouTube URL, MP4, WebM, or other video file link
            </p>
          </div>

          {/* Content */}
          <div className="space-y-4 p-5">
            <div>
              <label htmlFor="video-url" className="mb-2 block text-sm font-medium text-foreground">
                Video URL
              </label>
              <input
                id="video-url"
                type="text"
                value={url}
                onChange={(e) => {
                  setUrl(e.target.value);
                  setError("");
                }}
                onKeyDown={handleKeyDown}
                placeholder="https://youtube.com/watch?v=... or https://example.com/video.mp4"
                className="w-full rounded-lg border border-border bg-secondary px-3 py-2 text-sm text-foreground placeholder-muted-foreground transition-colors focus:outline-none focus:ring-2 focus:ring-ring"
                autoFocus
              />
            </div>

            {/* Error message */}
            {error && (
              <div className="flex items-start gap-2 rounded-lg bg-destructive/10 p-3">
                <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-destructive" />
                <p className="text-sm text-destructive">{error}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex gap-2 pt-2">
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="flex-1 rounded-lg border border-border bg-secondary px-4 py-2 font-medium text-foreground transition-colors hover:bg-secondary/80 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSubmit}
                className="flex-1 rounded-lg bg-primary px-4 py-2 font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-ring"
              >
                Embed Video
              </button>
            </div>
          </div>

          {/* Close button */}
          <Dialog.Close className="absolute right-2 top-2 inline-flex h-8 w-8 items-center justify-center rounded-md transition-colors hover:bg-secondary focus:outline-none">
            <X className="h-4 w-4" />
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
